import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'

import {
  LIMIT_RECIPES,
  MORSEL_PRO,
  MORSEL_PRO_FAMILY,
  PRICE_MORSEL_PRO,
} from '../../../utils/constants'
import { setShowModalTrial } from '../../../redux/actions/ui'
import { setUser } from '../../../redux/actions/user'
import { getUserInfo } from '../../../api/auth'
import { apiError, getBilling, upgradeCheckout } from '../../../api/account'
import { openCheckout } from '../../../utils/chargebee'
import { familyButtonText, priceText } from '../../../pages/AccountSettings/format'
import { FEATURES, Perks } from '../../../pages/AccountSettings/perks'
import routes from '../../../routers'

import './styles.css'

// Shown when a free account has used its 10 recipes and tries to add another
const ModalTrial = ({ show }) => {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.user?.data)
  const [billing, setBilling] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [paid, setPaid] = useState(false)

  useEffect(() => {
    if (!show) return
    setError('')
    setPaid(false)
    getBilling()
      .then(({ data }) => setBilling(data))
      .catch(() => setBilling(null))
  }, [show])

  const close = () => dispatch(setShowModalTrial(false))

  const usage = billing?.usage || user?.recipe_usage || {}
  const limit = usage.limit || LIMIT_RECIPES
  const used = Math.max(Number(usage.used ?? user?.recipes_count) || 0, limit)
  const locked = Number(usage.locked) || 0
  const pro = billing?.plans?.find((p) => p.id === MORSEL_PRO)
  const family = billing?.plans?.find((p) => p.id === MORSEL_PRO_FAMILY)

  const upgrade = (planId) => {
    setBusy(true)
    setError('')
    upgradeCheckout(planId)
      .then(({ data }) => openCheckout(billing?.chargebee_site, data))
      .then(async (result) => {
        if (!result?.paid) return
        setPaid(true)
        await getUserInfo()
          .then(({ data }) => dispatch(setUser(data)))
          .catch(() => null)
      })
      .catch((err) =>
        setError(apiError(err, 'Checkout is not available right now. Please try again.'))
      )
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      show={show}
      onHide={close}
      centered
      dialogClassName="upDialog"
      contentClassName="upContent"
      aria-labelledby="upgrade-title"
    >
      <button type="button" className="upClose" onClick={close} aria-label="Close">
        ×
      </button>

      {paid ? (
        <div className="upDone">
          <p className="upEyebrow">Morsel Pro</p>
          <h2 id="upgrade-title">Welcome to Pro!</h2>
          <p>Every recipe is unlocked. Add as many as you like.</p>
          <div className="upActions upCenter">
            <a className="upGold" href={routes.addRecipe}>
              Add a recipe
            </a>
            <button type="button" className="upGhostDark" onClick={close}>
              Close
            </button>
          </div>
        </div>
      ) : (
        <div className="upGrid">
          <div className="upDark">
            <p className="upEyebrow">
              {limit} of {limit} free recipes used
            </p>
            <div className="upMeter" role="img" aria-label={`${used} of ${limit} recipes used`}>
              <div />
            </div>
            <h2 id="upgrade-title">You're quite the chef!</h2>
            <p>
              {locked
                ? `Your free plan keeps your ${limit} newest recipes open. Your ${locked} older ones are locked, not deleted. Go Pro to open them all and keep adding.`
                : `You've filled your ${limit} free recipes. Go Pro to keep adding as many as you like. Everything you've saved stays right where it is.`}
            </p>
            <p className="upPrice">
              {pro ? priceText(pro) : `$${PRICE_MORSEL_PRO} per year`}. Cancel any time.
            </p>
            <div className="upActions">
              <button
                type="button"
                className="upGold"
                disabled={busy}
                onClick={() => upgrade(MORSEL_PRO)}
              >
                {busy ? 'Opening checkout…' : 'Go Pro'}
              </button>
              {family ? (
                <button
                  type="button"
                  className="upGhost"
                  disabled={busy}
                  onClick={() => upgrade(MORSEL_PRO_FAMILY)}
                >
                  {familyButtonText(family)}
                </button>
              ) : null}
            </div>
            {error ? <p className="upError">{error}</p> : null}
            <button type="button" className="upLater" onClick={close}>
              Not now, keep using Morsel free
            </button>
          </div>
          <div className="upLight">
            <p className="upEyebrow upEyebrowDark">What you get with Pro</p>
            <Perks items={FEATURES} />
          </div>
        </div>
      )}
    </Modal>
  )
}

export default ModalTrial
